import React, { useState } from 'react';
import { useAuthAdmin } from '../hooks/useAuthAdmin';
import Layout from './Layout';
import TransactionList from './TransactionList';
import TicketList from './TicketList';
import TicketTypeManagement from './TicketTypeManagement';
import KioskManagement from './KioskManagement';
import UserManagement from './UserManagement';

const Dashboard: React.FC = () => {
  const { isAuthenticated, isCheckingAuth, handleLogout } = useAuthAdmin();

  // Simple state management
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'transactions' | 'tickets' | 'ticket-types' | 'kiosks' | 'users'>('transactions');

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  return (
    <Layout
      title="Dashboard"
      subtitle="Management interface"
      onLogout={handleLogout}
    >
      {success && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{success}</span>
          <button
            onClick={clearSuccess}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={clearError}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'transactions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveView('transactions')}
          >
            📋 Transactions
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'tickets'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveView('tickets')}
          >
            🎫 Tickets
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'ticket-types'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveView('ticket-types')}
          >
            🏷️ Ticket Types
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'kiosks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveView('kiosks')}
          >
            🖥️ Kiosks
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveView('users')}
          >
            👤 Users
          </button>
        </div>
      </div>

      {/* Transactions View */}
      {activeView === 'transactions' && (
        <TransactionList />
      )}

      {/* Tickets View */}
      {activeView === 'tickets' && (
        <TicketList />
      )}

      {/* Ticket Types View */}
      {activeView === 'ticket-types' && (
        <TicketTypeManagement />
      )}

      {/* Kiosks View */}
      {activeView === 'kiosks' && (
        <KioskManagement />
      )}

      {/* Users View */}
      {activeView === 'users' && (
        <UserManagement />
      )}
    </Layout>
  );
};

export default Dashboard;
